import { getInstitutionalProfile } from "../institutional/institutional-profile-session";
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PhoneInput from './PhoneInput';
import OTPInput from './OTPInput';
import ProfileComplete from './ProfileComplete';
import EmailVerify from './EmailVerify';
import {
  sendOTP,
  resetSendOTP,
  verifyOTP,
  resetVerifyOTP,
  completeProfile,
  resetCompleteProfile,
  sendEmailOTP,
  resetSendEmailOTP,
  verifyEmailOTP,
  resetVerifyEmailOTP,
  userDetailsFromToken
} from '../../actions';
import { parsePhoneAuthError } from '../../utils/phoneValidation';
import { trackPhoneAuthStage } from '../../utils/tracking';
import { buildQLinkOAuthState, clearConsumedReferralParam, getSafeNextPath } from '../../utils/attribution';
import { getActiveReferralCode, clearReferralCode, getReferralCapturedAtForSignup } from '../../utils/referral';
import { isTokenPersistable, PENDING_EMAIL_VERIFY_KEY, getPendingEmailVerify } from '../../utils/session';
import { STALE_SESSION_KEYS } from '../../utils/stale-session';
import { forgetPhoneCodeStart, rememberPhoneCodeStart, startedPhoneCodeHere } from '../../utils/phone-code-start';
import { trackMobileProfileCompleted } from '../../services/mobileActivationAnalytics';
import mixpanel from 'mixpanel-browser';
import { POST_LOGIN_PATHS } from '../../constants/postLoginPaths';
import { QLINK_FUNNEL_EVENT, inAppFunnelProps, markQLinkSignInThisVisit, recordQLinkFunnelEvent } from '../../utils/qlink-funnel-events';

const AUTH_STATES = {
  PHONE_INPUT: 'PHONE_INPUT',
  OTP_VERIFY: 'OTP_VERIFY',
  PROFILE_COMPLETE: 'PROFILE_COMPLETE',
  EMAIL_VERIFY: 'EMAIL_VERIFY'
};

// Verified-email step (Akshay Jul 6: the phone flow's captured email must be
// PROVEN, not just typed). Build-time flag, dark until the QLearn-service
// email-OTP endpoints ship — flag off keeps today's capture-only behavior.
export const isVerifiedEmailEnabled = () => (
  process.env.REACT_APP_QLEARN_VERIFIED_EMAIL === 'true'
);

const LOOKUP_FAILED_MESSAGE = "We couldn't finish signing you in. Please try again.";

// Props (all optional; the /login mount passes only navigateOnSignIn={false}):
// - stayOnPage: mounted in the shared-question drawer. After sign-in, do NOT
//   navigate: the question page flips to signed-in on userDetailsFromToken and
//   shows the explanation, exactly like the drawer's Google popup path. Leaving
//   for /create-quiz lost the case the stranger signed in for (AUDIT fix #1).
// - qlinkId / qlinkPlatform: the shared link being viewed. verify-otp then
//   carries the same QLink attribution state the Google path puts in its OAuth
//   state, so the backend labels the session (AUDIT gap B3).
// - qlinkQuestionId / qlinkSurface ('teaser' | 'wall'): the question and the
//   drawer surface, for the first-party QLink funnel events. Those events are
//   recorded only in the drawer (stayOnPage with a qlinkId), never on /login.
// - navigateOnSignIn: /login passes false and navigates by itself once the
//   user is resolved (Daily, quiz-link and ?next= returns). Navigating here as
//   well moved the address first, so /login lost its ?next= and pushed Create
//   Quiz over the page the student came from.
// - onUseGoogle / useGoogleLabel: /login's web tabs pass their switch to the
//   Google tab and the button's words. The code screen then offers Google once
//   it has waited 30 s without a sign-in, for a number with no account yet.
//   The drawer and the native app pass nothing (no line).
const PhoneAuthSection = ({ stayOnPage = false, navigateOnSignIn = true, qlinkId, qlinkPlatform, qlinkQuestionId, qlinkSurface, onUseGoogle, useGoogleLabel } = {}) => {
  const [authState, setAuthState] = useState(AUTH_STATES.PHONE_INPUT);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState('');
  // The address awaiting verification (from the profile form, or from the
  // verify-otp response for returning users whose email is still unproven).
  const [pendingEmail, setPendingEmail] = useState('');
  // The user lookup this section started, while it is out: its request id and
  // the token this sign-in stored. Only its result finishes or fails the
  // sign-in: the store also resolves users for the sign-in page's other
  // methods, the drawer's Google popup, and pages from before this one.
  const phoneLookupRef = useRef(null);
  const [lookupFailed, setLookupFailed] = useState(false);
  // Whether the number sent a code has no account yet (the send's isNewUser;
  // unknown counts as new, and a resend does not change it). Google is offered
  // only then: Google reaches an existing phone account only through its
  // verified email, so for a returning number "Use Google instead" would be
  // refused or would open a second, empty account. The first send leaves an
  // unverified record, so later sends call the number known; a number this
  // browser was told is new, and has not verified since, still counts as new
  // (utils/phone-code-start.js says why the backend is not asked instead).
  const [codeForNewNumber, setCodeForNewNumber] = useState(false);

  const dispatch = useDispatch();
  const history = useHistory();

  // Redux state selectors
  const sendOTPState = useSelector((state) => state.sendOTP);
  const verifyOTPState = useSelector((state) => state.verifyOTP);
  const completeProfileState = useSelector((state) => state.completeProfile);
  const sendEmailOTPState = useSelector((state) => state.sendEmailOTP);
  const verifyEmailOTPState = useSelector((state) => state.verifyEmailOTP);
  const userDetailsFromTokenRes = useSelector((state) => state.userDetailsFromToken?.userDetails);
  const userDetailsFromTokenError = useSelector((state) => state.userDetailsFromToken?.errorMsg);
  const userDetailsFromTokenRequestId = useSelector((state) => state.userDetailsFromToken?.requestId);

  const lookUpSignedInUser = useCallback(() => {
    const lookup = userDetailsFromToken({ door: 'phone' });
    phoneLookupRef.current = { requestId: lookup.requestId, token: window.localStorage.getItem('token') };
    dispatch(lookup);
  }, [dispatch]);

  // Back to a fresh start: every step's request state cleared.
  const resetPhoneFlow = useCallback(() => {
    dispatch(resetSendOTP());
    dispatch(resetVerifyOTP());
    dispatch(resetCompleteProfile());
    dispatch(resetSendEmailOTP());
    dispatch(resetVerifyEmailOTP());
  }, [dispatch]);

  // A result that does not say which request it answers is taken as this
  // section's, as before.
  const answersOwnLookup = (requestId) => (
    requestId === undefined || requestId === phoneLookupRef.current?.requestId
  );

  const recordsQLinkFunnel = !!stayOnPage && !!qlinkId;
  const recordPhoneFunnel = (event, extra) => {
    if (!recordsQLinkFunnel) return;
    recordQLinkFunnelEvent({
      qlinkId,
      questionId: qlinkQuestionId,
      platform: qlinkPlatform || 'unknown',
      event,
      props: {
        method: 'phone',
        ...inAppFunnelProps(),
        ...(qlinkSurface === 'teaser' || qlinkSurface === 'wall' ? { surface: qlinkSurface } : {}),
        ...extra,
      },
    });
  };

  // Initialize and handle page reload with incomplete profile
  useEffect(() => {
    const token = window.localStorage.getItem('token');
    const isProfileComplete = window.localStorage.getItem('isProfileComplete');

    // Resume a pending email-OTP proof across reloads (GPT-5.6 finding 3):
    // the marker outlives isProfileComplete='true', so a reload mid-verify
    // lands back ON the verify screen instead of silently into the app.
    const pendingEmailStored = isVerifiedEmailEnabled() ? getPendingEmailVerify() : null;
    if (token && pendingEmailStored) {
      setPendingEmail(pendingEmailStored);
      dispatch(sendEmailOTP({ email: pendingEmailStored }));
      setAuthState(AUTH_STATES.EMAIL_VERIFY);
    } else if (token && isProfileComplete === 'false' && !getInstitutionalProfile()) {
      // If user reloaded page during phone auth flow with incomplete profile
      // User has token but profile is incomplete - show profile completion
      setAuthState(AUTH_STATES.PROFILE_COMPLETE);
    } else {
      // Track when phone auth is started (fresh session)
      trackPhoneAuthStage.started();
    }
  }, []);

  // Handle Send OTP success
  useEffect(() => {
    if (sendOTPState?.success && authState === AUTH_STATES.PHONE_INPUT) {
      if (sendOTPState.isNewUser === true) rememberPhoneCodeStart(currentPhoneNumber);
      setCodeForNewNumber(sendOTPState.isNewUser !== false || startedPhoneCodeHere(currentPhoneNumber));
      setAuthState(AUTH_STATES.OTP_VERIFY);
      trackPhoneAuthStage.otpSent(currentPhoneNumber, sendOTPState.isNewUser);
    }
  }, [sendOTPState, authState, currentPhoneNumber]);

  // Handle Send OTP error
  useEffect(() => {
    if (sendOTPState?.error) {
      trackPhoneAuthStage.otpFailed(sendOTPState.error);
      recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_RESULT, { result: 'failure', reason: 'otp_send_failed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOTPState?.error]);

  // Handle Verify OTP success
  useEffect(() => {
    if (verifyOTPState?.user && authState === AUTH_STATES.OTP_VERIFY) {
      const { accessToken, id, phoneNumber, isProfileComplete } = verifyOTPState.user;
      // Verified: from now on this number has an account, here as everywhere.
      forgetPhoneCodeStart(currentPhoneNumber);

      // Persist only a decodable JWT — a malformed server payload must not
      // brick app networking via the fetch interceptor (GPT-5.6 finding 6).
      if (!isTokenPersistable(accessToken)) {
        trackPhoneAuthStage.otpFailed('malformed-access-token');
        recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_RESULT, { result: 'failure', reason: 'otp_failed' });
        return;
      }

      // Store token immediately
      window.localStorage.setItem('token', accessToken);
      if (recordsQLinkFunnel) {
        markQLinkSignInThisVisit();
      }
      recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_RESULT, { result: 'success' });
      window.localStorage.setItem('userId', id);
      clearReferralCode(window.localStorage);
      clearConsumedReferralParam();

      // Store profile completion status for reload handling
      window.localStorage.setItem('isProfileComplete', isProfileComplete ? 'true' : 'false');

      // Track OTP verification success
      trackPhoneAuthStage.otpVerified(verifyOTPState.user);

      // Check if profile completion is needed (rely only on isProfileComplete, not isNewUser)
      if (!isProfileComplete) {
        // Profile incomplete - show profile completion screen
        setAuthState(AUTH_STATES.PROFILE_COMPLETE);
      } else if (
        isVerifiedEmailEnabled() &&
        verifyOTPState.user.emailVerified === false &&
        verifyOTPState.user.email
      ) {
        // Returning user whose email was captured but never proven: verify it
        // before entering the app. Strictly `=== false` — a backend that
        // doesn't send the field yet keeps today's behavior (dark-safe).
        window.localStorage.setItem('isProfileComplete', 'true');
        window.localStorage.setItem(PENDING_EMAIL_VERIFY_KEY, verifyOTPState.user.email);
        setPendingEmail(verifyOTPState.user.email);
        dispatch(sendEmailOTP({ email: verifyOTPState.user.email }));
        setAuthState(AUTH_STATES.EMAIL_VERIFY);
      } else {
        // Profile already complete - fetch full user details and redirect
        window.localStorage.setItem('isProfileComplete', 'true');
        lookUpSignedInUser();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOTPState, authState, dispatch]);

  // Handle Verify OTP error
  useEffect(() => {
    if (verifyOTPState?.error) {
      trackPhoneAuthStage.otpFailed(verifyOTPState.error);
      recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_RESULT, { result: 'failure', reason: 'otp_failed' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyOTPState?.error]);

  // Handle Complete Profile success
  useEffect(() => {
    if (completeProfileState?.success && authState === AUTH_STATES.PROFILE_COMPLETE) {
      // Track profile completion
      trackPhoneAuthStage.profileCompleted({ id: window.localStorage.getItem('userId') });
      // Mobile activation: a phone user finishing their profile now has an
      // email on file — a first-party activation milestone (no-op off native).
      trackMobileProfileCompleted();

      // Mark profile as complete
      window.localStorage.setItem('isProfileComplete', 'true');

      if (isVerifiedEmailEnabled() && pendingEmail) {
        // Prove the captured email before entering the app: send the code and
        // hold the userDetailsFromToken fetch (it triggers the redirect) until
        // verification succeeds. Persist the marker so a reload resumes HERE.
        window.localStorage.setItem(PENDING_EMAIL_VERIFY_KEY, pendingEmail);
        dispatch(sendEmailOTP({ email: pendingEmail }));
        setAuthState(AUTH_STATES.EMAIL_VERIFY);
      } else {
        // Profile completed, fetch full user details
        lookUpSignedInUser();
      }
    }
  }, [completeProfileState, authState, dispatch, pendingEmail, lookUpSignedInUser]);

  // Handle email verification success (verified-email step) — release the
  // held redirect.
  useEffect(() => {
    if (verifyEmailOTPState?.success && authState === AUTH_STATES.EMAIL_VERIFY) {
      window.localStorage.removeItem(PENDING_EMAIL_VERIFY_KEY);
      trackPhoneAuthStage.emailVerified({ id: window.localStorage.getItem('userId') });
      lookUpSignedInUser();
    }
  }, [verifyEmailOTPState, authState, lookUpSignedInUser]);

  // Handle userDetailsFromToken success (final step)
  useEffect(() => {
    if (userDetailsFromTokenRes?.id && answersOwnLookup(userDetailsFromTokenRequestId)) {
      phoneLookupRef.current = null;
      // Check if profile is complete before redirecting
      const isProfileComplete = window.localStorage.getItem('isProfileComplete');

      // If profile is incomplete, don't redirect - user should complete profile first
      if (isProfileComplete === 'false') {
        return;
      }

      // Identify user in active analytics platforms
      mixpanel.track('Sign In', {
        $name: userDetailsFromTokenRes.displayName,
        $user_id: userDetailsFromTokenRes.id,
        email: userDetailsFromTokenRes.email,
        role: userDetailsFromTokenRes.primaryRole,
        method: 'phone_otp'
      });
      mixpanel.identify(userDetailsFromTokenRes.id);
      mixpanel.people.set({
        $email: userDetailsFromTokenRes.email,
        $name: userDetailsFromTokenRes.displayName,
        $phone: currentPhoneNumber,
        $role: userDetailsFromTokenRes.primaryRole
      });

      // No session-replay call here — see login.jsx. Passing a bare pathname
      // dropped the query string and re-opened the recorder on a token URL.

      // Store additional user data
      const userData = {
        name: userDetailsFromTokenRes.displayName,
        email: userDetailsFromTokenRes.email
      };
      window.localStorage.setItem('userdetails', JSON.stringify(userData));
      window.localStorage.setItem('role', userDetailsFromTokenRes.primaryRole);

      // In the drawer the page itself reacts to the signed-in user; stay on
      // the question. On /login the sign-in page navigates.
      if (stayOnPage || !navigateOnSignIn) {
        return;
      }

      // Redirect only if profile is complete.
      history.push(
        getSafeNextPath(window.location.search, {
          allowedPathnames: POST_LOGIN_PATHS,
        }) || '/create-quiz'
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFromTokenRes, userDetailsFromTokenRequestId, history, currentPhoneNumber, stayOnPage, navigateOnSignIn]);

  // This section's lookup failed (an error, or a user without an id), so the
  // sign-in did not finish: it leaves no session, while the stored token is
  // still the one it stored, and starts again from the number, saying so.
  // Before, it stayed on the code screen with no word and kept the session.
  useEffect(() => {
    const lookup = phoneLookupRef.current;
    if (!lookup || userDetailsFromTokenRequestId !== lookup.requestId) return;
    const resolved = userDetailsFromTokenRes?.status === 200 || !!userDetailsFromTokenRes?.id;
    if (resolved || (!userDetailsFromTokenRes && !userDetailsFromTokenError)) return;
    phoneLookupRef.current = null;
    if (window.localStorage.getItem('token') === lookup.token) {
      STALE_SESSION_KEYS.forEach((key) => window.localStorage.removeItem(key));
    }
    resetPhoneFlow();
    setLookupFailed(true);
    setAuthState(AUTH_STATES.PHONE_INPUT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetailsFromTokenRes, userDetailsFromTokenError, userDetailsFromTokenRequestId]);

  // Cleanup on unmount
  useEffect(() => resetPhoneFlow, [resetPhoneFlow]);

  // Handlers
  const handleSendOTP = (data) => {
    setLookupFailed(false);
    setCurrentPhoneNumber(data.phoneNumber);
    recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_START);
    dispatch(sendOTP(data));
  };

  const handleVerifyOTP = (data) => {
    const referralCode = getActiveReferralCode(window.localStorage);
    const referralCapturedAt = referralCode
      ? getReferralCapturedAtForSignup(window.localStorage)
      : null;
    // Built at submit time (not mount) so it reads the freshest stored touch.
    const qlinkState = qlinkId
      ? buildQLinkOAuthState({ qlinkId, platform: qlinkPlatform || 'unknown' })
      : undefined;
    dispatch(verifyOTP({
      ...data,
      ...(referralCode && { referralCode }),
      ...(referralCapturedAt && { referralCapturedAt }),
      ...(qlinkState && { qlinkState }),
    }));
  };

  const handleResendOTP = (data) => {
    trackPhoneAuthStage.otpResent(data.phoneNumber);
    recordPhoneFunnel(QLINK_FUNNEL_EVENT.AUTH_START);
    dispatch(resetSendOTP());
    dispatch(resetVerifyOTP());
    dispatch(sendOTP(data));
  };

  const handleUseGoogle = () => {
    trackPhoneAuthStage.googleFallbackClicked();
    onUseGoogle();
  };

  const handleCompleteProfile = (data) => {
    setPendingEmail(data.email);
    dispatch(completeProfile(data));
  };

  const handleVerifyEmailOTP = (data) => {
    dispatch(verifyEmailOTP(data));
  };

  const handleResendEmailOTP = (data) => {
    dispatch(resetSendEmailOTP());
    dispatch(resetVerifyEmailOTP());
    dispatch(sendEmailOTP(data));
  };

  // "Wrong address?" — back to the profile form to re-submit with a corrected
  // email (completeProfile upserts name+email on the same account).
  const handleChangeEmail = () => {
    dispatch(resetCompleteProfile());
    dispatch(resetSendEmailOTP());
    dispatch(resetVerifyEmailOTP());
    setAuthState(AUTH_STATES.PROFILE_COMPLETE);
  };

  // Render current auth screen based on state
  const renderAuthScreen = () => {
    switch (authState) {
      case AUTH_STATES.PHONE_INPUT:
        return (
          <PhoneInput
            onSendOTP={handleSendOTP}
            loading={sendOTPState?.loading}
            error={
              (lookupFailed && LOOKUP_FAILED_MESSAGE)
              || (sendOTPState?.error ? parsePhoneAuthError(sendOTPState.error) : null)
            }
            cooldownUntil={sendOTPState?.cooldownUntil}
          />
        );

      case AUTH_STATES.OTP_VERIFY:
        return (
          <OTPInput
            phoneNumber={currentPhoneNumber}
            onVerifyOTP={handleVerifyOTP}
            onResendOTP={handleResendOTP}
            loading={verifyOTPState?.loading}
            error={verifyOTPState?.error ? parsePhoneAuthError(verifyOTPState.error) : null}
            remainingAttempts={verifyOTPState?.remainingAttempts}
            resendCooldown={60}
            onUseGoogle={onUseGoogle && codeForNewNumber ? handleUseGoogle : undefined}
            onGoogleFallbackShown={trackPhoneAuthStage.googleFallbackShown}
            useGoogleLabel={useGoogleLabel}
          />
        );

      case AUTH_STATES.PROFILE_COMPLETE:
        return (
          <ProfileComplete
            onCompleteProfile={handleCompleteProfile}
            loading={completeProfileState?.loading}
            error={completeProfileState?.error ? parsePhoneAuthError(completeProfileState.error) : null}
          />
        );

      case AUTH_STATES.EMAIL_VERIFY:
        return (
          <EmailVerify
            email={pendingEmail}
            onVerifyEmailOTP={handleVerifyEmailOTP}
            onResendEmailOTP={handleResendEmailOTP}
            onChangeEmail={handleChangeEmail}
            loading={verifyEmailOTPState?.loading || sendEmailOTPState?.loading}
            error={
              (verifyEmailOTPState?.error && parsePhoneAuthError(verifyEmailOTPState.error)) ||
              (sendEmailOTPState?.error && parsePhoneAuthError(sendEmailOTPState.error)) ||
              null
            }
            remainingAttempts={verifyEmailOTPState?.remainingAttempts}
            resendCooldown={60}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Box className="phone-auth-section">
      {renderAuthScreen()}
    </Box>
  );
};

export default PhoneAuthSection;
